import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { CloudRain, Sun, Cloud, CloudSnow, CloudLightning, Wind, Droplets, MapPin, AlertTriangle, CloudDrizzle, Check } from 'lucide-react';
import { useGameStore } from '../../store/gameStore';
import { updateTeam } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const WMO_CODES = {
  0: { label: 'Clear', icon: Sun, color: 'text-amber-400' },
  1: { label: 'Mostly Clear', icon: Sun, color: 'text-amber-300' },
  2: { label: 'Partly Cloudy', icon: Cloud, color: 'text-stone-300' },
  3: { label: 'Overcast', icon: Cloud, color: 'text-stone-400' },
  45: { label: 'Fog', icon: Cloud, color: 'text-stone-400' },
  48: { label: 'Fog', icon: Cloud, color: 'text-stone-400' },
  51: { label: 'Light Drizzle', icon: CloudDrizzle, color: 'text-blue-300' },
  53: { label: 'Drizzle', icon: CloudDrizzle, color: 'text-blue-400' },
  55: { label: 'Heavy Drizzle', icon: CloudRain, color: 'text-blue-500' },
  61: { label: 'Light Rain', icon: CloudRain, color: 'text-blue-400' },
  63: { label: 'Rain', icon: CloudRain, color: 'text-blue-500' },
  65: { label: 'Heavy Rain', icon: CloudRain, color: 'text-red-400' },
  71: { label: 'Light Snow', icon: CloudSnow, color: 'text-blue-200' },
  73: { label: 'Snow', icon: CloudSnow, color: 'text-blue-300' },
  75: { label: 'Heavy Snow', icon: CloudSnow, color: 'text-blue-400' },
  80: { label: 'Rain Showers', icon: CloudRain, color: 'text-blue-400' },
  81: { label: 'Rain Showers', icon: CloudRain, color: 'text-blue-500' },
  82: { label: 'Heavy Showers', icon: CloudRain, color: 'text-red-400' },
  95: { label: 'Thunderstorm', icon: CloudLightning, color: 'text-red-500' },
  96: { label: 'Thunderstorm + Hail', icon: CloudLightning, color: 'text-red-500' },
  99: { label: 'Severe Storm', icon: CloudLightning, color: 'text-red-600' },
};

const getWeatherInfo = (code) => WMO_CODES[code] || { label: 'Unknown', icon: Cloud, color: 'text-stone-400' };

export default function WeatherWidget() {
  const currentTeamId = useGameStore(s => s.currentTeamId);
  const [weather, setWeather] = useState(null);
  const [location, setLocation] = useState(() => {
    const saved = localStorage.getItem('dmp_weather_location');
    return saved ? JSON.parse(saved) : null;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (location) fetchWeather(location.lat, location.lon);
  }, [location]);

  // Keep the currently-selected team's weather_lat/lon in sync with the coach's location.
  // This lets parent / live-score pages fetch the same forecast without the coach's localStorage.
  const syncLocationToTeam = (loc) => {
    if (!currentTeamId || !loc?.lat || !loc?.lon) return;
    updateTeam(currentTeamId, {
      weather_lat: loc.lat,
      weather_lon: loc.lon,
      weather_location_name: [loc.name, loc.state].filter(Boolean).join(', '),
    }).catch(() => {});
  };

  const fetchWeather = async (lat, lon) => {
    try {
      const res = await fetch(`${API}/api/weather?lat=${lat}&lon=${lon}`);
      if (res.ok) setWeather(await res.json());
    } catch {}
  };

  const searchLocation = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearching(true);
    try {
      // Use zip code endpoint for numeric input, otherwise city search
      const isZip = /^\d{5}$/.test(q);
      const url = isZip
        ? `${API}/api/weather/geocode-zip?zip=${q}`
        : `${API}/api/weather/geocode?q=${encodeURIComponent(q)}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (isZip && data.lat) {
          // Zip returns a single location, auto-select it
          const locData = { name: data.name, state: data.state, country: 'US', lat: data.lat, lon: data.lon };
          setLocation(locData);
          localStorage.setItem('dmp_weather_location', JSON.stringify(locData));
          syncLocationToTeam(locData);
          setShowSetup(false);
          setSearchQuery('');
          return;
        }
        setResults(data.results || []);
      }
    } catch {} finally { setSearching(false); }
  };

  const selectLocation = (loc) => {
    const locData = { name: loc.name, state: loc.admin1, country: loc.country, lat: loc.latitude, lon: loc.longitude };
    setLocation(locData);
    localStorage.setItem('dmp_weather_location', JSON.stringify(locData));
    syncLocationToTeam(locData);
    setShowSetup(false);
    setResults([]);
    setSearchQuery('');
  };

  if (!location && !showSetup) {
    return (
      <Card className="border-0 shadow-md bg-gradient-to-r from-blue-900/40 to-stone-100 border-l-4 border-l-blue-500" data-testid="weather-setup-prompt">
        <CardContent className="py-3 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CloudRain size={20} className="text-blue-500" />
            <div>
              <p className="font-semibold text-sm text-stone-800">Game Day Weather</p>
              <p className="text-xs text-stone-500">Set your field location for auto weather checks</p>
            </div>
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white text-xs" onClick={() => setShowSetup(true)} data-testid="setup-weather-btn">
            <MapPin size={12} className="mr-1" /> Set Location
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (showSetup) {
    return (
      <Card className="border-0 shadow-md bg-white" data-testid="weather-search-card">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-blue-500" />
            <span className="font-semibold text-sm">Set Your Field Location</span>
          </div>
          <div className="flex gap-2">
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchLocation()}
              placeholder="Zip code (e.g., 78701)" className="text-sm" data-testid="weather-location-input" />
            <Button size="sm" onClick={searchLocation} disabled={searching} className="bg-blue-600 hover:bg-blue-500 text-white">
              {searching ? '...' : 'Search'}
            </Button>
          </div>
          {results.length > 0 && (
            <div className="space-y-1 max-h-[150px] overflow-y-auto">
              {results.map((r, i) => (
                <button key={i} onClick={() => selectLocation(r)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-blue-50 text-sm flex items-center gap-2 transition-colors"
                  data-testid={`location-result-${i}`}>
                  <MapPin size={12} className="text-blue-400 shrink-0" />
                  <span>{r.name}{r.admin1 ? `, ${r.admin1}` : ''} - {r.country}</span>
                </button>
              ))}
            </div>
          )}
          {location && (
            <button onClick={() => setShowSetup(false)} className="text-xs text-stone-500 hover:text-stone-700">Cancel</button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!weather?.current) return null;

  const current = weather.current;
  const daily = weather.daily;
  const code = current.weather_code;
  const info = getWeatherInfo(code);
  const Icon = info.icon;
  const temp = Math.round(current.temperature_2m);
  const rainProb = daily?.precipitation_probability_max?.[0] || 0;
  const isRainRisk = rainProb >= 40;
  const isSevere = [65, 82, 95, 96, 99].includes(code);

  return (
    <Card className={cn(
      "border-0 shadow-md overflow-hidden",
      isSevere ? "bg-gradient-to-r from-red-100 to-red-50 border-l-4 border-l-red-500" :
      isRainRisk ? "bg-gradient-to-r from-blue-100 to-blue-50 border-l-4 border-l-blue-500" :
      "bg-gradient-to-r from-amber-50 to-stone-50 border-l-4 border-l-green-500"
    )} data-testid="weather-widget">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Rain Alert Banner */}
            {(isRainRisk || isSevere) && (
              <div className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold animate-pulse",
                isSevere ? "bg-red-200 text-red-800" : "bg-blue-200 text-blue-800"
              )} data-testid="rain-alert">
                <AlertTriangle size={14} />
                {isSevere ? 'SEVERE WEATHER' : `${rainProb}% RAIN`}
              </div>
            )}
            <Icon size={28} className={info.color} />
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-stone-900">{temp}°F</span>
                <span className={cn("text-sm font-medium", info.color)}>{info.label}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-stone-500">
                <span className="flex items-center gap-0.5"><Wind size={10} /> {Math.round(current.wind_speed_10m)} mph</span>
                <span className="flex items-center gap-0.5"><Droplets size={10} /> {current.relative_humidity_2m}%</span>
                {daily && <span>Hi {Math.round(daily.temperature_2m_max[0])}° / Lo {Math.round(daily.temperature_2m_min[0])}°</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-xs text-stone-500 mb-1">
              <MapPin size={10} />
              <span>{location.name}{location.state ? `, ${location.state}` : ''}</span>
            </div>
            <button onClick={() => setShowSetup(true)} className="text-[10px] text-blue-500 hover:text-blue-700" data-testid="change-location-btn">change</button>
          </div>
        </div>

        {/* 3-day forecast */}
        {daily && (
          <div className="flex gap-3 mt-2 pt-2 border-t border-stone-200/50">
            {[0, 1, 2].map(i => {
              const dayInfo = getWeatherInfo(daily.weather_code[i]);
              const DayIcon = dayInfo.icon;
              const dayLabel = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : new Date(Date.now() + i * 86400000).toLocaleDateString('en', { weekday: 'short' });
              const dayRain = daily.precipitation_probability_max[i] || 0;
              return (
                <div key={i} className="flex-1 text-center" data-testid={`forecast-day-${i}`}>
                  <p className="text-[10px] text-stone-500">{dayLabel}</p>
                  <DayIcon size={16} className={cn("mx-auto my-0.5", dayInfo.color)} />
                  <p className="text-xs font-semibold text-stone-700">{Math.round(daily.temperature_2m_max[i])}°/{Math.round(daily.temperature_2m_min[i])}°</p>
                  {dayRain >= 30 && <p className="text-[9px] text-blue-500 font-bold">{dayRain}% rain</p>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
